import { LIVE_RELEASE_ENV } from '../create-run/constants';
import { METRIC_LABEL_MAP } from './band-config';
import type { RunDetail } from './types';

export function buildTooltipContent(contributingMetrics: unknown[] = []): string[] {
  return contributingMetrics.map((raw) => {
    if (typeof raw === 'string') {
      const info = METRIC_LABEL_MAP[raw];
      return info ? `${info.category} - ${info.label}` : raw;
    }
    if (raw && typeof raw === 'object') {
      const m = raw as { metricKey?: string | null; displayName?: string };
      const key = m.metricKey ?? null;
      if (key) {
        const info = METRIC_LABEL_MAP[key];
        return info ? `${info.category} - ${info.label}` : (m.displayName ?? key);
      }
      return m.displayName ?? String(raw);
    }
    return String(raw);
  });
}

function formatDateShort(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date}, ${time}`;
}

function parseYyyymmdd(part: string | undefined): string | null {
  if (!part) return null;
  const year = Number(part.slice(0, 4));
  const month = Number(part.slice(4, 6));
  const day = Number(part.slice(6, 8));
  const d = new Date(year, month - 1, day);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Ported from RunDetails.vue scopeLabel computed. Produces the one-line scope
 * summary for the header — "<env> Data (historical) | ..." or "Live data | ...".
 *
 * `getEnvLabel` is injected because the data-source env can be any EP pipeline
 * stage, and only the environment hook can resolve those labels.
 */
export function buildScopeLabel(
  run: RunDetail | undefined,
  getEnvLabel: (value: string | undefined | null) => string
): string {
  if (!run) return '-';
  const evalEnv = run.evaluationEnvironment;
  // Data source is decoupled from the evaluated env; fall back to the evaluated
  // env for legacy runs that predate the split.
  const sourceEnv = run.historicalEnvironment || evalEnv;
  const dataSetName = run.dataSetName;
  if (!evalEnv && !dataSetName) return '-';

  const isLive = evalEnv === LIVE_RELEASE_ENV || dataSetName?.startsWith('live_data');

  if (!isLive) {
    // Historical replay scope. dataSetName format: <prefix>_data_YYYYMMDD_YYYYMMDD.
    // The prefix is env-specific on EP bots, so pick out the date tokens by
    // shape rather than by fixed position.
    const sourceLabel = getEnvLabel(sourceEnv);
    const dateTokens = (dataSetName ?? '').split('_').filter((part) => /^\d{8}$/.test(part));
    const start = parseYyyymmdd(dateTokens[0]);
    const end = parseYyyymmdd(dateTokens[1]);
    if (start && end) {
      return `${sourceLabel} Data (historical) | ${start} - ${end}`;
    }
    return `${sourceLabel} Data (historical)`;
  }

  const sampleRate = run.scheduledDataPayload?.sampleRate;
  const sampleStr = sampleRate != null ? `${sampleRate}% sample` : null;
  const endStr = formatDateShort(run.endTime);

  if (!run.startTime) {
    // Start-now mode: no explicit start time.
    const window = endStr ? `Run Start - ${endStr}` : 'Run Start';
    return ['Live data', window, sampleStr].filter(Boolean).join(' | ');
  }

  const startStr = formatDateShort(run.startTime);
  const window = startStr && endStr ? `${startStr} – ${endStr}` : (startStr ?? endStr ?? '');
  return ['Live data', window, sampleStr].filter(Boolean).join(' | ');
}
